import React, { useState, useEffect, useCallback } from "react";
import { Home, Plus, Search, X } from "lucide-react";
import { ClassroomItem } from "./types";

interface BuildingOption {
  id: string;
  name: string;
  code: string;
}

export const ClassroomManagementPage: React.FC = () => {
  const [rooms, setRooms] = useState<ClassroomItem[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Modal Form State
  const [newRoomNumber, setNewRoomNumber] = useState("");
  const [newBuildingId, setNewBuildingId] = useState("");
  const [newCapacity, setNewCapacity] = useState(60);
  const [newFloor, setNewFloor] = useState(1);
  const [newRoomType, setNewRoomType] = useState("classroom");

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, []);

  const fetchRooms = useCallback(async (query?: string) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      let url = "/api/timetable/classrooms/";
      if (query) url += `?search=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        const results = Array.isArray(data) ? data : data.results || [];
        const mapped: ClassroomItem[] = results.map((r: {
          id: string;
          room_number?: string;
          building_name?: string;
          building_code?: string;
          room_type_display?: string;
          room_type?: string;
          capacity?: number;
          floor?: number;
        }) => ({
          id: r.id,
          roomNo: r.room_number || `Room ${r.id.slice(0, 4)}`,
          building: r.building_name
            ? `${r.building_name} (${r.building_code || ""}) - Floor ${r.floor || 1}`
            : `Floor ${r.floor || 1}`,
          type: (r.room_type_display || r.room_type || "Lecture Hall") as any,
          capacity: r.capacity || 60,
          hasAVEquipment: true,
        }));
        setRooms(mapped);
      } else {
        setErrorMsg("Failed to load classroom allocations from server.");
      }
    } catch (err) {
      console.warn("[ClassroomManagement] Fetch error:", err);
      setErrorMsg("Network error connecting to timetable service.");
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await fetch("/api/timetable/buildings/", { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        const results = Array.isArray(data) ? data : data.results || [];
        setBuildings(results.map((b: { id: string; name: string; code: string }) => ({ id: b.id, name: b.name, code: b.code })));
        if (results.length > 0) setNewBuildingId(results[0].id);
      }
    } catch (err) {
      console.warn("[ClassroomManagement] Buildings fetch error:", err);
    }
  }, [getHeaders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRooms(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchRooms]);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoomNumber.trim() || !newBuildingId) {
      alert("Please specify room number and select a building.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        room_number: newRoomNumber,
        building: newBuildingId,
        capacity: Number(newCapacity),
        floor: Number(newFloor),
        room_type: newRoomType,
      };
      const res = await fetch("/api/timetable/classrooms/", {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowAddModal(false);
        setNewRoomNumber("");
        fetchRooms(search);
      } else {
        const errData = await res.json();
        alert(`Error adding room: ${JSON.stringify(errData)}`);
      }
    } catch (err) {
      console.error("Create room error:", err);
      alert("Failed to submit classroom creation request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Home className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Classroom, Smart Lab & Hall Allocation</h2>
        </div>

        <button
          onClick={() => {
            fetchBuildings();
            setShowAddModal(true);
          }}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          <Plus className="w-4 h-4" />
          <span>Add Room</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter classrooms by room number, building code, or room type..."
          className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-xs font-medium focus:outline-none focus:border-indigo-500"
        />
      </div>

      {errorMsg && (
        <div className="p-3 bg-rose-950/40 border border-rose-800 text-rose-300 rounded-lg text-xs font-mono">
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500 font-mono">Loading classroom allocations...</div>
      ) : rooms.length === 0 ? (
        <div className="p-8 text-center text-slate-500 font-mono">
          No classroom allocations registered matching your query. Click &quot;Add Room&quot; to configure spaces.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rooms.map((r) => (
            <div key={r.id} className="p-4 bg-slate-950/90 border border-slate-800 rounded-xl space-y-2 font-mono">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-indigo-400 bg-indigo-950 px-2 py-0.5 rounded border border-indigo-900">
                  {r.roomNo}
                </span>
                <span className="text-[10px] font-bold text-cyan-400 bg-cyan-950 px-2 py-0.5 rounded border border-cyan-900 uppercase">
                  {r.type}
                </span>
              </div>
              <h3 className="font-bold text-slate-100 text-sm font-sans">{r.building}</h3>
              <div className="pt-2 border-t border-slate-800 flex justify-between text-slate-400 text-[10px]">
                <span>Capacity: {r.capacity} Seats</span>
                <span>AV Equipment: {r.hasAVEquipment ? "Yes" : "No"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Room Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4 text-xs">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="font-bold text-slate-100 text-sm">Configure New Classroom Space</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-200">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateRoom} className="space-y-3">
              <div>
                <label className="block text-slate-300 font-medium mb-1">Building</label>
                {buildings.length === 0 ? (
                  <p className="text-amber-400 text-[11px] italic">No active buildings registered. Please add a building in timetable settings first.</p>
                ) : (
                  <select
                    value={newBuildingId}
                    onChange={(e) => setNewBuildingId(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200"
                  >
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">Room Number / Identifier</label>
                <input
                  type="text"
                  value={newRoomNumber}
                  onChange={(e) => setNewRoomNumber(e.target.value)}
                  placeholder="e.g. 101, LAB-A, AUD-1"
                  required
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">Seating Capacity</label>
                  <input
                    type="number"
                    value={newCapacity}
                    onChange={(e) => setNewCapacity(Number(e.target.value))}
                    min={1}
                    required
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-medium mb-1">Floor Number</label>
                  <input
                    type="number"
                    value={newFloor}
                    onChange={(e) => setNewFloor(Number(e.target.value))}
                    min={0}
                    required
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">Space Type</label>
                <select
                  value={newRoomType}
                  onChange={(e) => setNewRoomType(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200"
                >
                  <option value="classroom">Classroom</option>
                  <option value="laboratory">Laboratory</option>
                  <option value="seminar_hall">Seminar Hall</option>
                  <option value="auditorium">Auditorium</option>
                </select>
              </div>

              <div className="pt-3 border-t border-slate-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || buildings.length === 0}
                  className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Save Classroom"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
